import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Trophy, Share2, Star, BarChart3, Download, Sparkles, Loader2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { toPng } from 'html-to-image';
import { preloadImagesForCapture } from '@/lib/preloadImagesForCapture';
import { useAuth } from '@/context/AuthContext';
import { RecapSquareCard } from '@/components/stats/RecapSquareCard';
import { formatGameDate } from '@/lib/formatGameDate';
import { DnpList, setPlayerPlayed } from '@/components/stats/PlayedToggle';
import { StatLegend } from '@/components/stats/StatLegend';
import { shareHero, recapPalette } from '@/lib/shareTheme';

const BOX_SCORE_CODES = ['PTS', 'FGM', 'FGA', '3PM', '3PA', 'FTM', 'FTA', 'OR', 'DR', 'AS', 'STL', 'BLK', 'TO', 'FG%'];

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Builds the Career Stat Sheet href for a player. When the game's owner has
// an active leaderboard share, every player name links there. Otherwise we
// render plain text (no broken link).
const careerHref = (token, teamName, playerName) => {
  if (!token || !playerName || !teamName) return null;
  const params = new URLSearchParams({ team: teamName, name: playerName });
  return `/stats/leaderboard/${token}/career?${params.toString()}`;
};

function PlayerNameLink({ href, name, children, dataTestid }) {
  // Wraps a player name in a link to the Career Stat Sheet when available.
  // Falls back to a plain span. The children prop lets us decorate with a
  // headshot avatar (used in PlayerRow) without forcing styling here.
  if (!href) return <>{children || name}</>;
  return (
    <Link
      to={href}
      data-testid={dataTestid}
      className="text-slate-800 hover:text-amber-600 hover:underline underline-offset-2 transition-colors">
      {children || name}
    </Link>
  );
}

function PlayerRow({ player, highlight, leaderboardToken, onMarkDnp }) {
  const fgPct = player.fga > 0 ? ((player.fgm / player.fga) * 100).toFixed(0) : '-';
  const href = careerHref(leaderboardToken, player.team_name, player.name);
  return (
    <tr className={`border-b border-slate-100 ${highlight ? 'bg-amber-50' : ''}`}>
      <td className="px-2 py-2 text-xs font-mono text-slate-500 text-center">{player.jersey}</td>
      <td className="px-2 py-2 text-sm font-semibold text-slate-800 sticky left-0 bg-inherit z-10">
        <span className="inline-flex items-center gap-2">
          {player.headshot_path && (
            <img src={`${API_URL}/api/files/${player.headshot_path}`} alt="" className="w-6 h-6 rounded-full object-cover border border-slate-200 shrink-0" />
          )}
          <PlayerNameLink href={href} name={player.name} dataTestid={`summary-row-player-${player.player_id}`}>
            <span>{player.name}</span>
          </PlayerNameLink>
          {highlight && <Star className="w-3 h-3 inline text-amber-500" />}
          {onMarkDnp && (
            <button
              type="button"
              onClick={() => onMarkDnp(player.player_id)}
              className="ml-1 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-slate-400 border border-slate-200 hover:text-rose-600 hover:border-rose-300 transition-colors"
              title="Did not play: remove from the box score and from games played"
              data-testid={`summary-mark-dnp-${player.player_id}`}
            >
              DNP
            </button>
          )}
        </span>
      </td>
      <td className="px-2 py-2 text-sm text-center font-bold text-[#00247D]">{player.pts}</td>
      <td className="px-2 py-2 text-xs text-center text-slate-600">{player.fgm}</td>
      <td className="px-2 py-2 text-xs text-center text-slate-600">{player.fga}</td>
      <td className="px-2 py-2 text-xs text-center text-slate-600">{player.three_m}</td>
      <td className="px-2 py-2 text-xs text-center text-slate-600">{player.three_a}</td>
      <td className="px-2 py-2 text-xs text-center text-slate-600">{player.ftm}</td>
      <td className="px-2 py-2 text-xs text-center text-slate-600">{player.fta}</td>
      <td className="px-2 py-2 text-xs text-center text-slate-600">{player.oreb}</td>
      <td className="px-2 py-2 text-xs text-center text-slate-600">{player.dreb}</td>
      <td className="px-2 py-2 text-sm text-center">{player.ast}</td>
      <td className="px-2 py-2 text-sm text-center">{player.stl}</td>
      <td className="px-2 py-2 text-sm text-center">{player.blk}</td>
      <td className="px-2 py-2 text-sm text-center text-red-500">{player.to}</td>
      <td className="px-2 py-2 text-xs text-center text-slate-500">{fgPct}%</td>
    </tr>
  );
}

// Renders the post-game Share Recap card. Captured to PNG by html-to-image.
// Delegates the heavy lifting to the shared RecapSquareCard component
// (used by both this basketball summary and the sport-agnostic
// MatchStats page) — this thin wrapper adapts the basketball data
// shape onto the shared props.
function RecapCard({
  innerRef, game, finalScore, topPerformers, date,
  eventName, eventLogoUrl,
  teamALogoUrl, teamBLogoUrl,
  organization,
}) {
  const scoreA = finalScore?.team_a ?? 0;
  const scoreB = finalScore?.team_b ?? 0;
  const winnerKey = scoreA > scoreB ? 'a' : scoreB > scoreA ? 'b' : null;
  const winnerName =
    winnerKey === 'a' ? game.team_a.name :
    winnerKey === 'b' ? game.team_b.name : null;
  const winnerMargin = winnerKey ? Math.abs(scoreA - scoreB) : 0;

  const asPerformer = (p) => ({
    id: p.player_id,
    jersey: p.jersey,
    name: p.name,
    primary: `${p.pts}p`,
    secondary: `${p.reb}r ${p.ast}a`,
  });

  return (
    <RecapSquareCard
      innerRef={innerRef}
      teamA={{ name: game.team_a.name }}
      teamB={{ name: game.team_b.name }}
      scoreA={scoreA}
      scoreB={scoreB}
      winnerKey={winnerKey}
      winnerLabel={winnerName ? `${winnerName} win by ${winnerMargin}` : null}
      headerLabel={eventName || organization || game.name || ''}
      dateLabel={date}
      subheading={`${game.team_a.name} vs ${game.team_b.name}`}
      teamAPerformers={(topPerformers?.team_a || []).map(asPerformer)}
      teamBPerformers={(topPerformers?.team_b || []).map(asPerformer)}
      eventLogoUrl={eventLogoUrl}
      teamALogoUrl={teamALogoUrl}
      teamBLogoUrl={teamBLogoUrl}
      theme={game.share_theme}
    />
  );
}

function TeamBoxScore({ teamName, teamLogo, players, topIds, leaderboardToken, teamKey, dnpPlayers, onSetPlayed, theme }) {
  const sum = (k) => players.reduce((s, p) => s + (p[k] || 0), 0);
  const totalPts = sum('pts');
  const totalFgm = sum('fgm');
  const totalFga = sum('fga');
  const total3m = sum('three_m');
  const total3a = sum('three_a');
  const totalFtm = sum('ftm');
  const totalFta = sum('fta');
  const totalOreb = sum('oreb');
  const totalDreb = sum('dreb');
  const totalAst = sum('ast');
  const totalStl = sum('stl');
  const totalBlk = sum('blk');
  const totalTo = sum('to');
  const teamFgPct = totalFga > 0 ? ((totalFgm / totalFga) * 100).toFixed(0) : '-';

  return (
    <Card className="overflow-hidden">
      <div className="px-4 py-3 flex items-center gap-3" style={shareHero(theme).band} data-testid={`bb-team-bar-${teamKey}`}>
        {teamLogo && (
          <img src={`${API_URL}/api/files/${teamLogo}`} alt="" className="w-8 h-8 rounded-full object-cover bg-white border border-white/20" />
        )}
        <h3 className="font-bold uppercase" style={{ fontFamily: 'Barlow Condensed' }}>{teamName}</h3>
      </div>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-left min-w-[820px]">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center w-10">#</th>
                <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase sticky left-0 bg-slate-50 z-10">Player</th>
                <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center">PTS</th>
                <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center">FGM</th>
                <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center">FGA</th>
                <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center">3PM</th>
                <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center">3PA</th>
                <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center">FTM</th>
                <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center">FTA</th>
                <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center">OR</th>
                <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center">DR</th>
                <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center">AS</th>
                <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center">STL</th>
                <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center">BLK</th>
                <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center">TO</th>
                <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center">FG%</th>
              </tr>
            </thead>
            <tbody>
              {players.map(p => (
                <PlayerRow key={p.player_id} player={p} highlight={topIds.includes(p.player_id)} leaderboardToken={leaderboardToken}
                  onMarkDnp={onSetPlayed ? (pid) => onSetPlayed(pid, false) : null} />
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50 font-bold text-sm">
                <td className="px-2 py-2"></td>
                <td className="px-2 py-2 text-slate-700 sticky left-0 bg-slate-50 z-10">TOTAL</td>
                <td className="px-2 py-2 text-center text-[#00247D]">{totalPts}</td>
                <td className="px-2 py-2 text-center text-xs">{totalFgm}</td>
                <td className="px-2 py-2 text-center text-xs">{totalFga}</td>
                <td className="px-2 py-2 text-center text-xs">{total3m}</td>
                <td className="px-2 py-2 text-center text-xs">{total3a}</td>
                <td className="px-2 py-2 text-center text-xs">{totalFtm}</td>
                <td className="px-2 py-2 text-center text-xs">{totalFta}</td>
                <td className="px-2 py-2 text-center text-xs">{totalOreb}</td>
                <td className="px-2 py-2 text-center text-xs">{totalDreb}</td>
                <td className="px-2 py-2 text-center">{totalAst}</td>
                <td className="px-2 py-2 text-center">{totalStl}</td>
                <td className="px-2 py-2 text-center">{totalBlk}</td>
                <td className="px-2 py-2 text-center text-red-500">{totalTo}</td>
                <td className="px-2 py-2 text-center text-xs">{teamFgPct}%</td>
              </tr>
            </tfoot>
          </table>
        </div>
        {onSetPlayed && (
          <DnpList players={dnpPlayers} teamKey={teamKey} onMarkPlayed={(pid) => onSetPlayed(pid, true)} />
        )}
      </CardContent>
    </Card>
  );
}

export default function StatsSummary() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recap, setRecap] = useState(null);
  const [recapLoading, setRecapLoading] = useState(false);
  const [minutes, setMinutes] = useState(null);
  const recapCardRef = useRef(null);
  const [sharingRecap, setSharingRecap] = useState(false);
  // Team + event logos are pre-fetched into base64 data URLs so the
  // Share Recap capture works on mobile Safari (where cross-origin <img>
  // fetches sometimes drop mid-serialization). Keeping the data URLs in
  // React state also means the recap card can mount with them inlined
  // Logos: we pass raw URLs straight to the RecapCard. The share-capture
  // pipeline (`preloadImagesForCapture(node)` in `buildRecapImage`) walks
  // every <img> and inlines it as a base-64 data URL IMMEDIATELY before
  // `html-to-image` serializes the DOM. That eliminates the race the old
  // "preload-into-React-state via useEffect" approach suffered from on
  // iPhone users who tap the share button before the useEffect finishes.

  // When the visitor arrives from a public share link
  // (e.g. /stats/summary/{id}?share={shareToken}), the back arrow should
  // return them to that public schedule rather than the auth-only /stats hub.
  const shareToken = searchParams.get('share') || '';
  const backHref = shareToken ? `/share/${shareToken}` : '/stats';

  useEffect(() => {
    fetchSummary();
    fetchMinutes();
  }, [gameId]);

  const fetchSummary = async () => {
    try {
      const res = await fetch(`${API_URL}/api/stats/games/${gameId}/summary`);
      if (res.ok) setSummary(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchMinutes = async () => {
    try {
      const res = await fetch(`${API_URL}/api/stats/games/${gameId}/minutes`);
      if (res.ok) {
        const data = await res.json();
        setMinutes(data.players || []);
      }
    } catch (e) {}
  };

  const generateRecap = async (force = false) => {
    setRecapLoading(true);
    try {
      const tz = (Intl.DateTimeFormat().resolvedOptions().timeZone) || '';
      const params = new URLSearchParams();
      if (tz) params.append('tz', tz);
      if (force) params.append('regenerate', 'true');
      const qs = params.toString();
      const res = await fetch(
        `${API_URL}/api/stats/games/${gameId}/ai-recap${qs ? `?${qs}` : ''}`,
        { method: 'POST' }
      );
      if (res.ok) {
        const data = await res.json();
        setRecap(data.recap);
      }
    } catch (e) { console.error(e); }
    finally { setRecapLoading(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#00247D] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!summary) return <div className="p-8 text-center text-slate-500">Game not found</div>;

  const { game, final_score, top_performers, all_players, key_moments = [], leaderboard_share_token: leaderboardToken, dnp_players = {} } = summary;

  // === Share Recap (post-game) ===========================================
  // Captures the off-screen RecapCard component to a 1080-wide PNG and fires
  // the Web Share API with the file (AirDrop / WhatsApp / Mail / etc).
  const fileName = `${game.team_a.name}-vs-${game.team_b.name}-recap.png`
    .replace(/\s+/g, '-').replace(/[^a-zA-Z0-9.-]/g, '');

  const buildRecapImage = async () => {
    if (!recapCardRef.current) return null;
    const node = recapCardRef.current;
    // Belt-and-braces: even though logos are already inlined as data
    // URLs in state, run the preload helper one more time in case a
    // future refactor adds a `<img>` to the card.
    const restoreImages = await preloadImagesForCapture(node);
    try {
      return await toPng(node, {
        backgroundColor: recapPalette(game.share_theme).base,
        cacheBust: true,
        pixelRatio: 2,
        width: 1080,
        height: 1080,
        canvasWidth: 1080,
        canvasHeight: 1080,
      });
    } finally {
      restoreImages();
    }
  };

  const dataUrlToBlob = async (dataUrl) => (await fetch(dataUrl)).blob();

  const handleShareRecap = async () => {
    if (sharingRecap) return;
    setSharingRecap(true);
    try {
      const dataUrl = await buildRecapImage();
      if (!dataUrl) return;
      const blob = await dataUrlToBlob(dataUrl);
      const file = new File([blob], fileName, { type: 'image/png' });
      const title = `${game.team_a.name} ${final_score.team_a} - ${game.team_b.name} ${final_score.team_b}`;
      if (navigator.canShare && navigator.canShare({ files: [file] }) && navigator.share) {
        try {
          await navigator.share({ files: [file], title });
          return;
        } catch (err) {
          if (err && err.name === 'AbortError') return;
        }
      }
      // Fallback: download
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = fileName;
      a.click();
      toast.success('Recap saved — share it from your photos');
    } catch (err) {
      console.error('share recap failed', err);
      toast.error('Couldn\u2019t generate recap image');
    } finally {
      setSharingRecap(false);
    }
  };
  const topAIds = top_performers.team_a.map(p => p.player_id);
  const topBIds = top_performers.team_b.map(p => p.player_id);
  // Owner-only edit button. Works for BOTH live-tracked and imported games —
  // if the owner edits a live game the backend converts it to post_game mode
  // (play-by-play → quarter=0 entries from the submitted totals) and wipes the
  // cached AI recap so the next Regenerate reflects the corrected numbers.
  const canEditStats = !!user && game?.user_id === user?.user_id;
  const isLiveTracked = game?.entry_mode !== 'post_game';

  const handleSetPlayed = async (playerId, played) => {
    try {
      await setPlayerPlayed(gameId, playerId, played);
      toast.success(played ? 'Marked as played' : 'Marked DNP. This game no longer counts toward their stats.');
      await fetchSummary();
    } catch (e) {
      toast.error(e.message);
    }
  };
  const onSetPlayed = canEditStats ? handleSetPlayed : null;

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden">
      <header className="glass-effect sticky top-0 z-50 border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-3 sm:px-6 h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Button variant="ghost" size="sm" className="shrink-0 px-2" onClick={() => navigate(backHref)} data-testid="stats-summary-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
            <h1 className="text-base sm:text-xl font-bold text-[#00247D] truncate" style={{ fontFamily: 'Barlow Condensed' }}>GAME SUMMARY</h1>
          </div>
          <div className="flex gap-1 sm:gap-2 shrink-0">
            {canEditStats && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (isLiveTracked && !window.confirm(
                    "Editing a live-tracked game replaces the play-by-play timeline with the totals you enter.\n\n" +
                    "Continue and correct the box score?"
                  )) return;
                  navigate(`/stats/import/${gameId}?edit=1`);
                }}
                className="border-[#00247D] text-[#00247D] hover:bg-blue-50 px-2 sm:px-3"
                data-testid="edit-stats-btn"
                title={isLiveTracked
                  ? "Correct the final box score (converts this game to post-game mode)"
                  : "Manually edit imported stats"}
              >
                <Pencil className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Edit Stats</span>
              </Button>
            )}
            <Button variant="outline" size="sm" className="px-2 sm:px-3" onClick={() => navigate(`/stats/flow/${gameId}${shareToken ? `?share=${shareToken}` : ''}`)} data-testid="analytics-btn">
              <BarChart3 className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Analytics</span>
            </Button>
            <Button size="sm" onClick={handleShareRecap}
              disabled={sharingRecap}
              className="font-bold hover:brightness-110 disabled:opacity-60 px-2 sm:px-3"
              style={shareHero(game.share_theme).accentFill}
              data-testid="share-recap-btn">
              {sharingRecap
                ? <><Loader2 className="w-4 h-4 sm:mr-1 animate-spin" /> <span className="hidden sm:inline">Generating</span></>
                : <><Share2 className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Share Recap</span></>}
            </Button>
            <Button variant="outline" size="sm" className="px-2 sm:px-3" onClick={() => window.open(`${API_URL}/api/stats/games/${gameId}/export-csv`, '_blank')} data-testid="export-csv-btn">
              <Download className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">CSV</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Score Header */}
        <Card className="mb-8 overflow-hidden">
          <div className="p-6 relative" style={shareHero(game.share_theme).band} data-testid="bb-score-header">
            <p className="text-xs uppercase opacity-60 mb-4 text-center">{game.name} &middot; {game.date}</p>
            <div className="flex items-center justify-between gap-4">
              {/* Team A logo — left edge */}
              <div className="flex-shrink-0 w-24 sm:w-36 md:w-48 flex justify-center">
                {game.team_a.logo ? (
                  <img
                    src={`${API_URL}/api/files/${game.team_a.logo}`}
                    alt={`${game.team_a.name} logo`}
                    className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 object-contain drop-shadow-lg"
                    data-testid="summary-team-a-logo"
                  />
                ) : game.event_id ? (
                  <a
                    href={`/schedule/${game.event_id}?tab=logos`}
                    className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 rounded-full border-2 border-dashed border-current opacity-50 hover:opacity-90 transition-opacity flex items-center justify-center text-[10px] uppercase tracking-wider text-center leading-tight px-3"
                    data-testid="summary-team-a-upload-logo"
                    title="Upload a logo for this team"
                  >
                    Add team logo
                  </a>
                ) : null}
              </div>

              {/* Score — center */}
              <div className="flex items-center justify-center gap-6 flex-1">
                <div className="text-right">
                  <div className="text-xs sm:text-sm uppercase opacity-70 tracking-wider truncate max-w-[14rem]">{game.team_a.name}</div>
                  <div className="text-5xl sm:text-6xl font-black tabular-nums leading-none drop-shadow" data-testid="final-score-a">{final_score.team_a}</div>
                </div>
                <div className="text-2xl opacity-30 font-bold">—</div>
                <div className="text-left">
                  <div className="text-xs sm:text-sm uppercase opacity-70 tracking-wider truncate max-w-[14rem]">{game.team_b.name}</div>
                  <div className="text-5xl sm:text-6xl font-black tabular-nums leading-none drop-shadow" data-testid="final-score-b">{final_score.team_b}</div>
                </div>
              </div>

              {/* Team B logo — right edge */}
              <div className="flex-shrink-0 w-24 sm:w-36 md:w-48 flex justify-center">
                {game.team_b.logo ? (
                  <img
                    src={`${API_URL}/api/files/${game.team_b.logo}`}
                    alt={`${game.team_b.name} logo`}
                    className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 object-contain drop-shadow-lg"
                    data-testid="summary-team-b-logo"
                  />
                ) : game.event_id ? (
                  <a
                    href={`/schedule/${game.event_id}?tab=logos`}
                    className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 rounded-full border-2 border-dashed border-current opacity-50 hover:opacity-90 transition-opacity flex items-center justify-center text-[10px] uppercase tracking-wider text-center leading-tight px-3"
                    data-testid="summary-team-b-upload-logo"
                    title="Upload a logo for this team"
                  >
                    Add team logo
                  </a>
                ) : null}
              </div>
            </div>
            {game.status === 'completed' && (
              <p className="text-xs opacity-50 mt-4 uppercase text-center tracking-wider">
                Final{game.entry_mode === 'post_game' ? ' (Box Score Import)' : ''}
              </p>
            )}
          </div>
        </Card>

        {/* Top Performers */}
        <div className="mb-8">
          <h2 className="text-lg font-bold text-[#00247D] mb-4 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
            <Trophy className="w-5 h-5 text-amber-500" /> TOP PERFORMERS
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { team: game.team_a.name, logo: game.team_a.logo, players: top_performers.team_a },
              { team: game.team_b.name, logo: game.team_b.logo, players: top_performers.team_b }
            ].map(({ team, logo, players }) => (
              <Card key={team}>
                <CardContent className="p-4">
                  <h4 className="text-xs font-bold text-slate-500 uppercase mb-3 flex items-center gap-2">
                    {logo && <img src={`${API_URL}/api/files/${logo}`} alt="" className="w-5 h-5 rounded-full object-cover" />}
                    {team}
                  </h4>
                  <div className="space-y-3">
                    {players.map((p, i) => {
                      const href = careerHref(leaderboardToken, team, p.name);
                      return (
                        <div key={p.player_id} className="flex items-center gap-3">
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                            {i + 1}
                          </div>
                          {p.headshot_path && (
                            <img src={`${API_URL}/api/files/${p.headshot_path}`} alt="" className="w-8 h-8 rounded-full object-cover border border-slate-200" />
                          )}
                          <div className="flex-1">
                            <PlayerNameLink href={href} name={p.name} dataTestid={`top-performer-${p.player_id}`}>
                              <span className="font-semibold text-sm text-slate-800 hover:text-amber-600 hover:underline underline-offset-2">#{p.jersey} {p.name}</span>
                            </PlayerNameLink>
                          </div>
                          <div className="text-right text-xs text-slate-500">
                            <span className="font-bold text-[#00247D] text-sm mr-2">{p.pts}pts</span>
                            {p.reb}reb {p.ast}ast
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Key Moments & Player Minutes sections removed per user request.
            The Share Recap PNG below uses the same data internally. */}

        {/* Full Box Scores */}
        <div className="space-y-6">
          <TeamBoxScore teamName={game.team_a.name} teamLogo={game.team_a.logo} players={all_players.team_a} topIds={topAIds} leaderboardToken={leaderboardToken}
            teamKey="a" dnpPlayers={dnp_players.team_a} onSetPlayed={onSetPlayed} theme={game.share_theme} />
          <TeamBoxScore teamName={game.team_b.name} teamLogo={game.team_b.logo} players={all_players.team_b} topIds={topBIds} leaderboardToken={leaderboardToken}
            teamKey="b" dnpPlayers={dnp_players.team_b} onSetPlayed={onSetPlayed} theme={game.share_theme} />
        </div>
        <StatLegend sport="basketball" codes={BOX_SCORE_CODES} testId="box-score-legend" />

        {/* Player Minutes section removed per user request.
            (Kept the data fetch so the Share Recap card can still pull MIN+/-) */}

        {/* AI Game Recap */}
        <Card className="mt-6" data-testid="ai-recap-section">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-[#00247D] uppercase" style={{ fontFamily: 'Barlow Condensed' }}>AI Game Recap</h3>
              <div className="flex items-center gap-2">
                {!recap && (
                  <Button size="sm" onClick={() => generateRecap(false)} disabled={recapLoading} className="bg-[#00247D] hover:bg-[#001A5C]" data-testid="generate-recap-btn">
                    {recapLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                    {recapLoading ? 'Generating...' : 'Generate Recap'}
                  </Button>
                )}
                {recap && canEditStats && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => generateRecap(true)}
                    disabled={recapLoading}
                    className="border-[#00247D] text-[#00247D] hover:bg-blue-50"
                    data-testid="regenerate-recap-btn"
                    title="Delete the cached recap and generate a fresh one from the current box score"
                  >
                    {recapLoading
                      ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Regenerating...</>
                      : <><Sparkles className="w-3.5 h-3.5 mr-1" /> Regenerate</>}
                  </Button>
                )}
              </div>
            </div>
            {recap ? (
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap" data-testid="recap-text">{recap}</p>
            ) : (
              <p className="text-sm text-slate-400">Click "Generate Recap" for an AI-written game summary.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Off-screen Recap Card mount — html-to-image captures this DOM node when
          the user taps "Share Recap". Positioned absolutely far off-screen so
          it doesn't shift the layout, but still rendered fully (visibility:
          hidden / display:none would prevent measurement). */}
      <div aria-hidden="true" style={{ position: 'fixed', left: '-99999px', top: 0, pointerEvents: 'none' }}>
        <RecapCard
          innerRef={recapCardRef}
          game={game}
          finalScore={final_score}
          topPerformers={top_performers}
          date={game.date ? formatGameDate(game.date) : ''}
          organization={game.event_organization || user?.organization}
          eventLogoUrl={
            (game.event_logo || user?.organization_logo)
              ? `${API_URL}/api/files/${game.event_logo || user?.organization_logo}`
              : ''
          }
          teamALogoUrl={game.team_a?.logo ? `${API_URL}/api/files/${game.team_a.logo}` : ''}
          teamBLogoUrl={game.team_b?.logo ? `${API_URL}/api/files/${game.team_b.logo}` : ''}
          eventName={game.event_name || game.name}
        />
      </div>
    </div>
  );
}
